/**
 * Shared bureau identity helpers for credit-report tradelines.
 *
 * Backend may expose bureau reporting via any of:
 * - reported_bureaus / reportedBureaus
 * - bureaus_reported / bureausReported
 * - bureau_data / bureauData (per-field maps)
 * - singular bureau / bureau_code
 */

export const CANONICAL_BUREAUS = ['TransUnion', 'Experian', 'Equifax'];

const EMPTY_VALUES = new Set(['', '--', '-', 'n/a', 'na', 'null', 'undefined', 'unknown']);

/**
 * Normalize a bureau label or code to a canonical display name.
 * @returns {'TransUnion'|'Experian'|'Equifax'|null}
 */
export function normalizeBureauName(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase().replace(/[\s_-]+/g, '');
  if (!s || EMPTY_VALUES.has(s)) return null;
  if (s === 'tu' || s === 'transunion' || s === 'trans' || s === 'tuc') return 'TransUnion';
  if (s === 'ex' || s === 'experian' || s === 'exp' || s === 'xp') return 'Experian';
  if (s === 'eq' || s === 'equifax' || s === 'eqf' || s === 'efx') return 'Equifax';
  return null;
}

function isPresentBureauValue(value) {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (EMPTY_VALUES.has(trimmed.toLowerCase())) return false;
  }
  return true;
}

function collectFromList(list, into) {
  if (!Array.isArray(list)) return;
  for (const item of list) {
    const normalized = normalizeBureauName(item);
    if (normalized && !into.includes(normalized)) into.push(normalized);
  }
}

function deriveFromBureauData(bureauData) {
  if (!bureauData || typeof bureauData !== 'object') return [];
  return CANONICAL_BUREAUS.filter((bureau) =>
    Object.values(bureauData).some((fieldData) => {
      if (!fieldData || typeof fieldData !== 'object' || Array.isArray(fieldData)) return false;
      const value = fieldData[bureau] ?? fieldData[bureau.toLowerCase()];
      return isPresentBureauValue(value);
    })
  );
}

/**
 * Resolve which bureaus actually report this tradeline.
 * Never invents all three when evidence is missing.
 */
export function getTradelineReportedBureaus(tradeline) {
  if (!tradeline || typeof tradeline !== 'object') return [];

  const reported = [];
  collectFromList(tradeline.reported_bureaus, reported);
  collectFromList(tradeline.reportedBureaus, reported);
  collectFromList(tradeline.bureaus_reported, reported);
  collectFromList(tradeline.bureausReported, reported);
  collectFromList(tradeline.bureaus_reporting, reported);
  if (Array.isArray(tradeline.bureaus)) {
    collectFromList(tradeline.bureaus, reported);
  }
  if (reported.length) return reported;

  for (const key of ['bureau', 'bureau_code', 'bureauCode']) {
    const normalized = normalizeBureauName(tradeline[key]);
    if (normalized) return [normalized];
  }

  const fromBureauData = deriveFromBureauData(
    tradeline.bureau_data || tradeline.bureauData
  );
  if (fromBureauData.length) return fromBureauData;

  const nested = tradeline.bureaus;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const fromNested = CANONICAL_BUREAUS.filter(
      (bureau) => nested[bureau] || nested[bureau.toLowerCase()]
    );
    if (fromNested.length) return fromNested;
  }

  return [];
}

export function tradelineReportsBureau(tradeline, bureau) {
  const target = normalizeBureauName(bureau);
  if (!target) return false;
  return getTradelineReportedBureaus(tradeline).some((b) => b === target);
}

/**
 * Bureau dropdown filter: keep rows that report the selected bureau.
 */
export function tradelineMatchesBureauFilter(tradeline, selectedBureau) {
  if (!selectedBureau || selectedBureau === 'all') return true;
  return tradelineReportsBureau(tradeline, selectedBureau);
}
